'use client';

import { ArtistsParallax } from '@/components/features/sections/artists-parallax';
import { SectionShell } from '@/components/ui/section-shell';
import { motion } from 'framer-motion';
import Image, { StaticImageData } from 'next/image';
import { FaQuoteLeft } from 'react-icons/fa';
import { testimonials } from '../constants';
export default function IndustryTrust() {
  return <div className="flex flex-col py-12" data-sentry-component="IndustryTrust" data-sentry-source-file="industry-trust.tsx">
      <SectionShell id="testimonials" className="relative py-0" data-sentry-element="SectionShell" data-sentry-source-file="industry-trust.tsx">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <motion.h2 className="text-3xl md:text-4xl font-bold mb-6" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-sentry-element="unknown" data-sentry-source-file="industry-trust.tsx">
              Trusted by Industry Leaders
            </motion.h2>
            <motion.div className="w-20 h-1 bg-primary mx-auto mb-8" initial={{
            width: 0
          }} whileInView={{
            width: 80
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} data-sentry-element="unknown" data-sentry-source-file="industry-trust.tsx" />
            <motion.p className="text-lg text-muted-foreground" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} data-sentry-element="unknown" data-sentry-source-file="industry-trust.tsx">
              Join the community of professional producers and artists already
              using our loops
            </motion.p>
          </div>

          {/* Featured testimonials */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
            {testimonials.map((testimonial, index) => <TestimonialCard key={index} quote={testimonial.quote} author={testimonial.author} position={testimonial.position} achievements={testimonial.achievements} image={testimonial.image} index={index} />)}
          </div>

          {/* Artist logos parallax */}
          <div className="mt-20">
            <p className="text-center text-lg text-muted-foreground">
              Our sounds have been used by major artist and generated{' '}
              <span className="text-primary font-semibold">
                2 billion+ streams
              </span>
            </p>
          </div>
        </div>
      </SectionShell>

      <ArtistsParallax data-sentry-element="ArtistsParallax" data-sentry-source-file="industry-trust.tsx" />
    </div>;
}
interface TestimonialCardProps {
  quote: string;
  author: string;
  position: string;
  achievements: string;
  image: StaticImageData;
  index: number;
  variant?: 'default' | 'compact';
}
export function TestimonialCard({
  quote,
  author,
  position,
  achievements,
  image,
  index,
  variant = 'default'
}: TestimonialCardProps) {
  const isCompact = variant === 'compact';
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.3,
    delay: index * 0.05
  }} className={`
        group relative bg-card/50 backdrop-blur-sm border border-border
        hover:border-primary/30 rounded-lg overflow-hidden transition-all
        duration-300 flex flex-col
        ${isCompact ? 'p-3' : 'p-4'}
      `} data-sentry-element="unknown" data-sentry-component="TestimonialCard" data-sentry-source-file="industry-trust.tsx">
      {/* Quote icon */}
      {!isCompact && <div className="absolute top-4 right-4 text-primary/20 group-hover:text-primary/30 transition-colors duration-300">
          <FaQuoteLeft size={32} />
        </div>}

      {/* Quote content */}
      <div className={`relative z-10 ${isCompact ? 'mb-2' : 'mb-3'}`}>
        <p className="text-sm text-muted-foreground group-hover:text-foreground transition-colors duration-300">
          "
          {isCompact ? quote.length > 80 ? `${quote.substring(0, 80)}...` : quote : quote.length > 100 ? `${quote.substring(0, 100)}...` : quote}
          "
        </p>
      </div>

      {/* Author info */}
      <div className="flex items-center gap-2 mt-auto">
        <div className={`rounded-full overflow-hidden bg-primary/10 flex-shrink-0 ${isCompact ? 'w-7 h-7' : 'w-8 h-8'}`}>
          {image ? <Image src={image} alt={author} width={isCompact ? 28 : 32} height={isCompact ? 28 : 32} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-primary">
              {author.charAt(0)}
            </div>}
        </div>

        <div>
          <h4 className="font-semibold text-sm text-foreground">{author}</h4>
          <div className="flex flex-col sm:flex-row sm:items-center gap-x-1">
            <p className="text-xs text-muted-foreground">{position}</p>
            {achievements && <>
                <span className="hidden sm:inline text-muted">·</span>
                <p className="text-xs text-primary">{achievements}</p>
              </>}
          </div>
        </div>
      </div>
    </motion.div>;
}